import { useFilterSites } from '../context/sitesContext';
import { useTemplateState } from '../context/templateContext';
import { useFormContext, useWatch } from 'react-hook-form';

import { Box, Button, Divider, Grid, Stack, Switch, Typography, useTheme } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { LightTooltip } from 'src/components';
import { ASSET_REPORT_DEFAULT_COLUMNS, DATA_REPORT_DEFAULT_COLUMNS } from '../config/config';
import { TrashBinIcon } from 'src/assets/icons/TrashBinIcon';
import { CustomWaterLoading } from 'src/components/streametric/custom-water-loading/CustomWaterLoading';
import ColumnToggle from '../components/sidebar/columns/ToggleColumn';
import { useResponsive } from 'src/hooks/useResponsive';
import AssetPropertiesList from '../components/asset-report-sidebar/asset-properties-list/AssetPropertiesList';
import { StyledFormWrapper } from './style';
import { allTimezones, useTimezoneSelect } from 'react-timezone-select';
import { AssetReportBaseLevelFormItems } from '../components/asset-report-form-sections/AssetReportBaseLevelFormItems';
import { useState } from 'react';
import { ApiResourceState, ReportType } from '../types/types';
import { useSnackbar } from 'notistack';
import useQueryParamsActions from 'src/hooks/useQueryParamsActions';
import { useAuthentication } from 'src/features/authentication/context';
import { ReportsService } from 'src/services/reports/reports.service';
import { parseReportTemplatePayload } from '../utils/parseReportTemplatePayload';
import { useTemplateCache } from '../context/templateCacheContext';
import { isAdminOrSiteManager } from 'src/utilities/permissions';
import { DataReportBaseLevelFormItems } from '../components/data-report-form-sections/DataReportBaseLevelFormItems';
import DataColumnList from '../components/data-report-sidebar/data-columns-list/DataColumnList';
import { ReportFormBtnControl } from '../components/report-form-btns/ReportFormBtnControl';
import { MemoSelectedFiltersList } from '../components/selected-filters-list/SelectedFiltersList';

type BaseProps = {
  title: string;
  onUndo: (sites: any) => void;
  disableActions: boolean;
  onSubmit: () => void;
  reportType: ReportType;
  isFilterBtnActive: boolean;
};

type AssetReportProps = BaseProps & {
  reportType: ReportType.ASSET;
  toggleSidebar: (state: boolean) => void;
  applyFiltersHandler: any;
  clearAllFiltersHandler: () => void;
};

type DataReportProps = BaseProps & {
  reportType: ReportType.DATA;
  toggleSidebar?: never;
  applyFiltersHandler?: never;
  clearAllFiltersHandler?: never;
};

type ReportsNewFormProps = AssetReportProps | DataReportProps;

const ReportsNewForm = (props: ReportsNewFormProps) => {
  const { title, onUndo, onSubmit, reportType, isFilterBtnActive, disableActions } = props;
  const {
    getCurrentRole,
    customerId: { value: customerId },
    siteId: { value: siteId },
  } = useAuthentication();
  const methods = useFormContext();
  const { trigger } = methods;
  const { setValue, watch, getValues } = useFormContext();
  const { checkTemplateForChanges } = useTemplateState();
  const { isLoading } = useFilterSites();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const currentRole = getCurrentRole(customerId, siteId);
  const disabledPermisionSaveBtn = isAdminOrSiteManager(currentRole);

  const theme = useTheme();
  const isMobile = useResponsive('down', 'md');
  const isTablet = useResponsive('down', 'lg');
  const isExtraLarge = useResponsive('down', 'xl');
  const labelStyle = 'original';
  const data = getValues();
  const { enqueueSnackbar } = useSnackbar();
  const [error, setError] = useState<boolean>(false);

  const templateCacheState = useTemplateCache();

  const { get: getQueryParam, append } = useQueryParamsActions();
  const templateId = getQueryParam('templateId');

  const { options } = useTimezoneSelect({ labelStyle, timezones: allTimezones });
  const testId = `${data?.header?.reportType?.id?.toLowerCase()}-report-time-zone`;

  const toggleColumn = (name: string, value: boolean) => {
    setValue(`data.columns.default.${name}`, value);
  };

  const [reportState, setReportState] = useState<{
    status: ApiResourceState;
    error: Error | null;
    data: any;
  }>({
    status: 'INITIAL',
    error: null,
    data: null,
  });

  const renderDefaultColumns = () => {
    const columns =
      reportType === ReportType.ASSET ? ASSET_REPORT_DEFAULT_COLUMNS : DATA_REPORT_DEFAULT_COLUMNS;

    return columns.map((columnItem) => (
      <Grid item key={columnItem.name} xs={12} sm={12} md={6} lg={4} xl={2}>
        <ColumnToggle
          name={columnItem.name}
          label={columnItem.label}
          enabled={(watch('data.columns.default') || {})[columnItem.name]}
          onToggle={toggleColumn}
          testId={`${columnItem.name}-toggle`}
        />
      </Grid>
    ));
  };
  const aggregationInterval = useWatch({ name: 'data.aggregationInterval' });

  const renderBaseLevelFormItems = () => {
    if (reportType === ReportType.ASSET) {
      const { toggleSidebar } = props;
      return (
        <AssetReportBaseLevelFormItems
          testId={testId}
          theme={theme}
          options={options}
          toggleSidebar={toggleSidebar}
          isFilterBtnActive={isFilterBtnActive}
          watch={watch}
          setValue={setValue}
          isMobile={isMobile}
          setError={setError}
        />
      );
    }

    return (
      <DataReportBaseLevelFormItems
        testId={testId}
        options={options}
        isMobile={isMobile}
        setError={setError}
      />
    );
  };

  const renderReportColumnList = () => {
    const customColumns = watch('data.columns.custom') || [];

    if (reportType === ReportType.ASSET) {
      return (
        <AssetPropertiesList
          testId="add-column-asset-report"
          assetProperties={customColumns}
          onChange={(assetProperties) => setValue('data.columns.custom', assetProperties)}
        />
      );
    }

    return (
      <DataColumnList
        dataColumns={customColumns}
        currentSelectedAggregationInterval={aggregationInterval}
        onChange={(newDataColumns) => setValue('data.columns.custom', newDataColumns)}
      />
    );
  };

  const onSaveHandler = async () => {
    const isValid = await trigger();

    if (!isValid) {
      return enqueueSnackbar('Please fill out all required fields', { variant: 'error' });
    }
    if (!data.data.timeZone) {
      return enqueueSnackbar('Timezone is required!', { variant: 'error' });
    }
    setReportState((prevState) => ({ ...prevState, status: 'LOADING' }));
    try {
      const report = templateId
        ? await ReportsService.updateReportTemplate(
            templateId,
            customerId!,
            parseReportTemplatePayload(data.header.reportType?.id, {
              ...data,
              title,
            })
          )
        : await ReportsService.saveReportTemplate(
            customerId!,
            parseReportTemplatePayload(data.header.reportType?.id, { ...data, title })
          );
      templateCacheState[1]({});
      append('templateId', report.id);
      setReportState((prevState) => ({ ...prevState, data: report, status: 'SUCCESS' }));
      enqueueSnackbar('Report template successfully ' + (templateId ? 'updated' : 'saved') + '!', {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(
        'Could not ' + (templateId ? 'update' : 'save') + ' report template!\n' + err.message,
        { variant: 'error' }
      );
      setReportState((prevState) => ({ ...prevState, error: err, status: 'ERROR' }));
    }
  };
  const hasNoChanges = checkTemplateForChanges(methods.watch());

  let cols = watch('data.columns.custom');
  if (!Array.isArray(cols)) cols = [];

  const isSavedDisabled =
    reportState.status === 'LOADING' ||
    hasNoChanges === 'INVALID' ||
    hasNoChanges ||
    disableActions ||
    isLoading ||
    disabledPermisionSaveBtn ||
    error;
  const isGenerateDisabled =
    reportState.status === 'LOADING' ||
    hasNoChanges === 'INVALID' ||
    !hasNoChanges ||
    disableActions ||
    isLoading ||
    cols.length === 0 ||
    error;
  return (
    <StyledFormWrapper
      theme={theme}
      isTablet={isTablet}
      isExtraLarge={isExtraLarge}
      isPdfReport={false}
    >
      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            minHeight: '350px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomWaterLoading />
        </Box>
      ) : (
        <Box width="100%">
          <Grid container spacing={2} mb={2} alignItems="center">
            {renderBaseLevelFormItems()}
            {isFilterBtnActive && reportType === ReportType.ASSET && (
              <Grid item sm="auto">
                <Button
                  onClick={props.clearAllFiltersHandler}
                  color="error"
                  variant="text"
                  startIcon={<TrashBinIcon fill={theme.palette.error.main} />}
                >
                  Clear All
                </Button>
              </Grid>
            )}
            {isFilterBtnActive && reportType === ReportType.ASSET && (
              <Grid item xs={12}>
                <MemoSelectedFiltersList
                  applyFiltersHandler={props.applyFiltersHandler}
                  isLoading={isLoading}
                />
              </Grid>
            )}
          </Grid>

          {reportType === ReportType.ASSET && (
            <Box mb={2}>
              <Typography variant="subtitle1" fontWeight={500} gutterBottom>
                Default Columns
              </Typography>
              <Grid container spacing={2} sx={{ width: '100%' }}>
                {renderDefaultColumns()}
              </Grid>
            </Box>
          )}

          <Divider sx={{ marginBlock: '16px' }} />

          {renderReportColumnList()}

          {reportType === ReportType.ASSET && (
            <Box mt={3}>
              {(() => {
                const isDaily = watch('data.aggregationInterval') === 'DAILY';
                const tooltipTitle = isDaily
                  ? 'Automatically generates an AI-powered summary of the report, highlighting key insights and trends across your asset data.'
                  : 'AI Summary is only available for Daily aggregation interval.';
                return (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      border: '1px solid',
                      borderColor: 'action.disabled',
                      borderRadius: '8px',
                      pl: '6px',
                      height: '36px',
                      boxSizing: 'border-box',
                      opacity: isDaily ? 1 : 0.5,
                    }}
                  >
                    <LightTooltip
                      placement="top"
                      arrow
                      enterTouchDelay={0}
                      title={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 400,
                            fontSize: '14px',
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                          }}
                        >
                          {tooltipTitle}
                        </Typography>
                      }
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 24,
                          height: 24,
                          borderRadius: '50%',
                          backgroundColor: 'action.selected',
                          flexShrink: 0,
                        }}
                      >
                        <AutoAwesomeIcon sx={{ width: 14, height: 14 }} />
                      </Box>
                    </LightTooltip>
                    <Switch
                      checked={!!watch('data.generateAiSummary')}
                      onChange={(e) => setValue('data.generateAiSummary', e.target.checked)}
                      disabled={!isDaily}
                      inputProps={{ 'data-sm': 'generate-ai-summary-toggle' } as any}
                    />
                  </Box>
                );
              })()}
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2} mt={4}>
            <ReportFormBtnControl
              isGenerateDisabled={isGenerateDisabled}
              isSavedDisabled={isSavedDisabled}
              onSaveHandler={onSaveHandler}
              reportState={reportState}
              onUndo={onUndo}
              onSubmit={onSubmit}
              showTooltip={isGenerateDisabled && isSavedDisabled && cols.length === 0}
            />
          </Stack>
        </Box>
      )}
    </StyledFormWrapper>
  );
};

export default ReportsNewForm;
